import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { AdminLayout } from '../components/AdminLayout';
import { KPICard } from '../components/KPICard';
import { useGlobalOverview, useVisitorAnalytics } from '../hooks/useAdminStats';
import { useAdminFilter } from '../components/AdminSidebar';
import { useTheme } from '@/contexts/ThemeContext';
import {
  Users,
  UserCheck,
  ArrowLeftRight,
  Wallet,
  DollarSign,
  Target,
  PieChart,
  RefreshCw,
  Eye,
  TrendingUp,
  Calendar,
  BarChart3,
} from 'lucide-react';

function formatNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toLocaleString('fr-FR');
}

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M €';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K €';
  return n.toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });
}

export default function GlobalOverviewPage() {
  const { excludeAdmins } = useAdminFilter();
  const { data, isLoading, error, refetch } = useGlobalOverview(excludeAdmins);
  const { data: visitorData, refetch: refetchVisitors } = useVisitorAnalytics(excludeAdmins);
  const { isDarkMode } = useTheme();

  const handleRefresh = () => {
    refetch();
    refetchVisitors();
  };

  const usersChartOption = useMemo(() => {
    if (!data?.users_per_month) return {};
    const months = data.users_per_month.map((d) => d.month);
    const counts = data.users_per_month.map((d) => d.count);

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: isDarkMode ? '#1f2937' : '#fff',
        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
        textStyle: { color: isDarkMode ? '#f9fafb' : '#111827' },
      },
      grid: { top: 20, right: 20, bottom: 30, left: 50 },
      xAxis: {
        type: 'category',
        data: months,
        axisLine: { lineStyle: { color: isDarkMode ? '#374151' : '#e5e7eb' } },
        axisLabel: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 11 },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: isDarkMode ? '#1f2937' : '#f3f4f6' } },
        axisLabel: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 11 },
      },
      series: [
        {
          data: counts,
          type: 'bar',
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#16c47f' },
                { offset: 1, color: '#16c47f80' },
              ],
            },
          },
          emphasis: { itemStyle: { color: '#16c47f' } },
        },
      ],
    };
  }, [data, isDarkMode]);

  const transactionsChartOption = useMemo(() => {
    if (!data?.transactions_per_month) return {};
    const months = data.transactions_per_month.map((d) => d.month);
    const counts = data.transactions_per_month.map((d) => d.count);
    const amounts = data.transactions_per_month.map((d) => d.total_amount);

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: isDarkMode ? '#1f2937' : '#fff',
        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
        textStyle: { color: isDarkMode ? '#f9fafb' : '#111827' },
      },
      legend: {
        data: ['Transactions', 'Volume (€)'],
        bottom: 0,
        textStyle: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 11 },
      },
      grid: { top: 20, right: 60, bottom: 40, left: 50 },
      xAxis: {
        type: 'category',
        data: months,
        axisLine: { lineStyle: { color: isDarkMode ? '#374151' : '#e5e7eb' } },
        axisLabel: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 11 },
      },
      yAxis: [
        {
          type: 'value',
          name: 'Transactions',
          axisLine: { show: false },
          splitLine: { lineStyle: { color: isDarkMode ? '#1f2937' : '#f3f4f6' } },
          axisLabel: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 11 },
        },
        {
          type: 'value',
          name: 'Volume',
          axisLine: { show: false },
          splitLine: { show: false },
          axisLabel: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 11 },
        },
      ],
      series: [
        {
          name: 'Transactions',
          data: counts,
          type: 'bar',
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: '#16c47f',
          },
        },
        {
          name: 'Volume (€)',
          data: amounts,
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          lineStyle: { color: '#8b5cf6', width: 3 },
          itemStyle: { color: '#8b5cf6' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#8b5cf620' },
                { offset: 1, color: '#8b5cf600' },
              ],
            },
          },
        },
      ],
    };
  }, [data, isDarkMode]);

  const visitorsChartOption = useMemo(() => {
    if (!visitorData?.visitors_per_day) return {};
    const days = visitorData.visitors_per_day.map((d) => d.day);
    const visitors = visitorData.visitors_per_day.map((d) => d.visitors);
    const views = visitorData.visitors_per_day.map((d) => d.page_views);

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: isDarkMode ? '#1f2937' : '#fff',
        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
        textStyle: { color: isDarkMode ? '#f9fafb' : '#111827' },
      },
      legend: {
        data: ['Visiteurs', 'Pages vues'],
        bottom: 0,
        textStyle: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 11 },
      },
      grid: { top: 20, right: 20, bottom: 40, left: 50 },
      xAxis: {
        type: 'category',
        data: days,
        axisLine: { lineStyle: { color: isDarkMode ? '#374151' : '#e5e7eb' } },
        axisLabel: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 11 },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: isDarkMode ? '#1f2937' : '#f3f4f6' } },
        axisLabel: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 11 },
      },
      series: [
        {
          name: 'Visiteurs',
          data: visitors,
          type: 'bar',
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: '#3b82f6',
          },
        },
        {
          name: 'Pages vues',
          data: views,
          type: 'line',
          smooth: true,
          lineStyle: { color: '#f59e0b', width: 3 },
          itemStyle: { color: '#f59e0b' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#f59e0b20' },
                { offset: 1, color: '#f59e0b00' },
              ],
            },
          },
        },
      ],
    };
  }, [visitorData, isDarkMode]);

  if (isLoading) {
    return (
      <AdminLayout title="Global Overview" subtitle="Vue d'ensemble de clevia">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 animate-pulse"
            >
              <div className="w-11 h-11 bg-gray-200 dark:bg-gray-700 rounded-xl mb-4" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20 mb-2" />
              <div className="h-7 bg-gray-200 dark:bg-gray-700 rounded w-16" />
            </div>
          ))}
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6 h-80 animate-pulse"
            >
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-40 mb-4" />
              <div className="h-full bg-gray-100 dark:bg-gray-700/50 rounded-xl" />
            </div>
          ))}
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Global Overview" subtitle="Vue d'ensemble de clevia">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-8 text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-xl hover:bg-red-200 dark:hover:bg-red-900/50 transition"
          >
            <RefreshCw className="w-4 h-4" /> Réessayer
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Global Overview"
      subtitle="Vue d'ensemble de clevia"
      action={
        <button
          onClick={handleRefresh}
          className="p-2 rounded-xl text-gray-400 hover:text-[#16c47f] hover:bg-[#16c47f]/10 transition-all"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      }
    >
      {/* ── Platform KPIs ── */}
      <div className="mb-8">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3 flex items-center gap-2">
          <Users className="w-3.5 h-3.5" /> Plateforme
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <KPICard
            title="Total Utilisateurs"
            value={formatNumber(data?.total_users ?? 0)}
            icon={Users}
            gradient="from-blue-500 to-blue-600"
          />
          <KPICard
            title="Utilisateurs Actifs"
            value={formatNumber(data?.active_users ?? 0)}
            icon={UserCheck}
            gradient="from-emerald-500 to-emerald-600"
            subtitle="30 derniers jours"
          />
          <KPICard
            title="Transactions"
            value={formatNumber(data?.total_transactions ?? 0)}
            icon={ArrowLeftRight}
            gradient="from-violet-500 to-violet-600"
          />
          <KPICard
            title="Comptes"
            value={formatNumber(data?.total_accounts ?? 0)}
            icon={Wallet}
            gradient="from-amber-500 to-amber-600"
          />
          <KPICard
            title="Argent Suivi"
            value={formatCurrency(data?.total_money_tracked ?? 0)}
            icon={DollarSign}
            gradient="from-[#16c47f] to-emerald-600"
          />
          <KPICard
            title="Budgets"
            value={formatNumber(data?.total_budgets ?? 0)}
            icon={PieChart}
            gradient="from-pink-500 to-pink-600"
          />
          <KPICard
            title="Objectifs"
            value={formatNumber(data?.total_goals ?? 0)}
            icon={Target}
            gradient="from-cyan-500 to-cyan-600"
          />
        </div>
      </div>

      {/* ── Traffic KPIs ── */}
      <div className="mb-8">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3 flex items-center gap-2">
          <Eye className="w-3.5 h-3.5" /> Trafic
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Visiteurs aujourd'hui"
            value={formatNumber(visitorData?.visitors_today ?? 0)}
            icon={Eye}
            gradient="from-sky-500 to-sky-600"
            change={
              visitorData?.visitors_yesterday
                ? Math.round(
                    ((visitorData.visitors_today - visitorData.visitors_yesterday) /
                      visitorData.visitors_yesterday) *
                      100
                  )
                : undefined
            }
            subtitle="vs hier"
          />
          <KPICard
            title="Visiteurs cette semaine"
            value={formatNumber(visitorData?.visitors_this_week ?? 0)}
            icon={Calendar}
            gradient="from-indigo-500 to-indigo-600"
          />
          <KPICard
            title="Visiteurs ce mois"
            value={formatNumber(visitorData?.visitors_this_month ?? 0)}
            icon={TrendingUp}
            gradient="from-rose-500 to-rose-600"
          />
          <KPICard
            title="Pages vues aujourd'hui"
            value={formatNumber(visitorData?.page_views_today ?? 0)}
            icon={BarChart3}
            gradient="from-orange-500 to-orange-600"
          />
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Users per month */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            Utilisateurs par mois
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">Inscriptions mensuelles</p>
          {data?.users_per_month?.length ? (
            <ReactECharts option={usersChartOption} style={{ height: 280 }} />
          ) : (
            <div className="h-[280px] flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
              Aucune donnée disponible
            </div>
          )}
        </div>

        {/* Transactions per month */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            Transactions par mois
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
            Volume et nombre de transactions
          </p>
          {data?.transactions_per_month?.length ? (
            <ReactECharts option={transactionsChartOption} style={{ height: 280 }} />
          ) : (
            <div className="h-[280px] flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
              Aucune donnée disponible
            </div>
          )}
        </div>

        {/* Daily Visitors */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            Visiteurs par jour
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
            30 derniers jours — visiteurs uniques et pages vues
          </p>
          {visitorData?.visitors_per_day?.length ? (
            <ReactECharts option={visitorsChartOption} style={{ height: 280 }} />
          ) : (
            <div className="h-[280px] flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
              Aucune donnée disponible
            </div>
          )}
        </div>
      </div>

      {/* Top Pages */}
      {visitorData?.top_pages?.length ? (
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            Pages les plus visitées
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">Top 10 des pages</p>
          <div className="space-y-2">
            {visitorData.top_pages.map((p, i) => (
              <div key={p.page} className="flex items-center gap-3">
                <span className="text-xs font-medium text-gray-400 dark:text-gray-500 w-5 text-right">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                      {p.page}
                    </span>
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 ml-2">
                      {formatNumber(p.views)}
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-sky-400 rounded-full"
                      style={{ width: `${(p.views / visitorData.top_pages![0].views) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </AdminLayout>
  );
}
